<script lang="ts">
    import { globalState, Page } from "./state.svelte";
    import { checkLibraryEmpty } from "./db";

    import Card from "@smui/card";
    import Grid, { Cell } from "@smui/layout-grid";
    import Textfield from "@smui/textfield";
    import IconButton from "@smui/icon-button";
    import SvgIcon from "./SvgIcon.svelte";
    import {
        mdiCheck,
        mdiUpload,
        mdiLinkVariant,
        mdiBookmarkMultipleOutline,
    } from "@mdi/js";
    import Button, { Label } from "@smui/button";
    import Snackbar, { Label as SbLabel } from "@smui/snackbar";

    let files: FileList | null = $state(null);
    let url = $state("");

    enum FileSource {
        file,
        url,
    }

    let snackbar: Snackbar;

    async function loadFile(source: FileSource, event?: SubmitEvent) {
        event?.preventDefault();
        if (source === FileSource.file) {
            globalState.file = await loadFromFileInput();
        } else {
            try {
                let splitUrl = new URL(url).pathname.split("/");
                globalState.filename = decodeURIComponent(
                    splitUrl[splitUrl.length - 1] ||
                        // use the second last part if the url ends with /
                        splitUrl[splitUrl.length - 2],
                );

                let result = await fetch(url);
                globalState.file = await result.arrayBuffer();
            } catch (e) {
                console.error(e);
                snackbar.open();
            }
        }
        if (globalState.file.byteLength !== 0) {
            globalState.storedInLibrary = false;
            globalState.currentPage = Page.extender;
        }
    }

    function loadFromFileInput(): Promise<ArrayBuffer> {
        return new Promise((resolve) => {
            if (!files) {
                return;
            }
            let reader = new FileReader();
            reader.onload = (e) => {
                let result = e.target?.result;
                if (result && typeof result === "object") resolve(result);
            };
            reader.onerror = (e) => {
                console.error(e.target?.error);
                alert("Could not read the file");
            };
            globalState.filename = files[0].name;
            reader.readAsArrayBuffer(files[0]);
        });
    }

    $effect(() => {
        if (files) loadFile(FileSource.file);
    });

    checkLibraryEmpty();
</script>

<div>
    <Card>
        <Grid>
            <Cell span={12}>
                <p>Choose an audio file to extend</p>
            </Cell>
            <Cell spanDevices={{ desktop: 3, tablet: 2, phone: 4 }}>
                <Button class="full-size" variant="outlined" tag="label">
                    <SvgIcon icon={mdiUpload} />
                    <Label>Upload a file</Label>
                    <input type="file" bind:files accept="audio/*,video/*" />
                </Button>
            </Cell>
            <Cell
                spanDevices={{ desktop: 1, tablet: 1, phone: 4 }}
                align="middle"
            >
                <span class="or">or</span>
            </Cell>
            <Cell spanDevices={{ desktop: 3, tablet: 2, phone: 4 }}>
                <form onsubmit={(e) => loadFile(FileSource.url, e)}>
                    <Textfield
                        bind:value={url}
                        label="URL"
                        variant="outlined"
                        style="width: 100%;"
                        type="url"
                        required
                    >
                        {#snippet leadingIcon()}
                            <SvgIcon
                                class="mdc-text-field__icon mdc-text-field__icon--leading"
                                icon={mdiLinkVariant}
                            />
                        {/snippet}
                        {#snippet trailingIcon()}
                            <IconButton touch style="margin: auto;">
                                <SvgIcon icon={mdiCheck} />
                            </IconButton>
                        {/snippet}
                    </Textfield>
                </form>
            </Cell>
            <Cell
                spanDevices={{ desktop: 1, tablet: 1, phone: 4 }}
                align="middle"
            >
                <span class="or">or</span>
            </Cell>
            <Cell spanDevices={{ desktop: 4, tablet: 2, phone: 4 }}>
                {#if !globalState.isLibraryEmpty}
                    <Button
                        variant="outlined"
                        class="full-size"
                        onclick={() => (globalState.currentPage = Page.library)}
                    >
                        <SvgIcon icon={mdiBookmarkMultipleOutline} />
                        <Label>Choose from library</Label>
                    </Button>
                {:else}
                    <Button variant="outlined" class="full-size" disabled>
                        <SvgIcon icon={mdiBookmarkMultipleOutline} />
                        <Label>Library is empty</Label>
                    </Button>
                {/if}
            </Cell>
        </Grid>
    </Card>
</div>

<Snackbar bind:this={snackbar} timeoutMs={8000}>
    <SbLabel>
        Could not read the file, because the server does not allow it. Please
        download and upload it manually.
    </SbLabel>
</Snackbar>

<style>
    * :global(.full-size) {
        width: 100%;
        height: 100%;
    }

    input[type="file"] {
        display: none;
    }
</style>
